'use client';
import React from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

const workshops = [
  {
    id: 'ws-001',
    icon: 'MegaphoneIcon',
    category: 'Advocacy',
    title: 'Advocacy & Civic Engagement',
    description: 'Learn to advocate effectively for causes you believe in through evidence-based argumentation and community organizing.',
    duration: '6 weeks',
    level: 'Beginner',
    levelColor: 'bg-emerald-100 text-emerald-700',
    date: 'Sep 6, 2026',
    spots: 20,
  },
  {
    id: 'ws-002',
    icon: 'ChatBubbleLeftRightIcon',
    category: 'Communication',
    title: 'Intro to Public Speaking',
    description: 'Build your foundation in public speaking — learn to speak clearly, confidently, and persuasively in front of any audience.',
    duration: '4 weeks',
    level: 'Beginner',
    levelColor: 'bg-emerald-100 text-emerald-700',
    date: 'Sep 8, 2026',
    spots: 25,
  },
  {
    id: 'ws-003',
    icon: 'AcademicCapIcon',
    category: 'Communication',
    title: 'Advanced Public Speaking',
    description: 'Elevate your speaking skills — master advanced delivery techniques, complex argumentation, and high-stakes presentations.',
    duration: '6 weeks',
    level: 'Advanced',
    levelColor: 'bg-rose-100 text-rose-700',
    date: 'Sep 13, 2026',
    spots: 18,
  },
  {
    id: 'ws-004',
    icon: 'StarIcon',
    category: 'Leadership',
    title: 'Leadership Development',
    description: 'Build confidence and lead with purpose — develop the mindset and skills of an effective youth leader.',
    duration: '8 weeks',
    level: 'Intermediate',
    levelColor: 'bg-amber-100 text-amber-700',
    date: 'Sep 15, 2026',
    spots: 18,
  },
  {
    id: 'ws-005',
    icon: 'BoltIcon',
    category: 'Communication',
    title: 'Impromptu Speaking',
    description: 'Think on your feet and speak with confidence in any situation — from classroom discussions to public forums.',
    duration: '3 weeks',
    level: 'Beginner',
    levelColor: 'bg-emerald-100 text-emerald-700',
    date: 'Sep 22, 2026',
    spots: 20,
  },
  {
    id: 'ws-006',
    icon: 'NewspaperIcon',
    category: 'Media',
    title: 'Media Literacy & Policy Analysis',
    description: 'Critically evaluate media sources, understand policy implications, and become an informed civic participant.',
    duration: '5 weeks',
    level: 'Intermediate',
    levelColor: 'bg-amber-100 text-amber-700',
    date: 'Sep 10, 2026',
    spots: 22,
  },
  {
    id: 'ws-007',
    icon: 'PencilSquareIcon',
    category: 'Writing',
    title: 'Persuasive Writing',
    description: 'Craft compelling arguments in writing — from op-eds and policy briefs to persuasive essays and advocacy letters.',
    duration: '4 weeks',
    level: 'Intermediate',
    levelColor: 'bg-amber-100 text-amber-700',
    date: 'Sep 17, 2026',
    spots: 20,
  },
];

export default function FeaturedWorkshops() {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 2xl:px-16">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-12">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-semibold mb-3">
              <Icon name="AcademicCapIcon" size={14} variant="outline" />
              Our Workshops
            </div>
            <h2 className="text-hero-md font-extrabold text-foreground tracking-tight">
              Start Your Advocacy Journey
            </h2>
          </div>
          <Link href="/workshop-listings" className="btn-secondary whitespace-nowrap">
            Register for a Workshop
            <Icon name="ArrowRightIcon" size={16} variant="outline" />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {workshops.slice(0, 3).map((ws) => (
            <div key={ws.id} className="card-base p-6 card-hover flex flex-col">
              <div className="flex items-start justify-between mb-4">
                <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Icon name={ws.icon as any} size={20} variant="outline" className="text-primary" />
                </div>
                <span className={`badge-skill ${ws.levelColor}`}>{ws.level}</span>
              </div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">
                {ws.category}
              </p>
              <h3 className="text-base font-bold text-foreground mb-2">{ws.title}</h3>
              <p className="text-sm text-zinc-500 leading-relaxed mb-4 flex-1 line-clamp-3">
                {ws.description}
              </p>
              <div className="flex items-center gap-4 text-xs text-muted-foreground mb-4">
                <span className="flex items-center gap-1">
                  <Icon name="ClockIcon" size={13} variant="outline" />
                  {ws.duration}
                </span>
                <span className="flex items-center gap-1">
                  <Icon name="CalendarIcon" size={13} variant="outline" />
                  {ws.date}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">{ws.spots} spots available</span>
                <Link href="/workshop-listings" className="btn-primary px-4 py-2 text-xs">
                  Register
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}